import { createApp, h } from 'vue'
import MessageBoxComponent from '../components/ui/common/MessageBoxComponent.vue'

class MessageBoxService {

  private static instance: any = null
  private static container: HTMLDivElement | null = null

  private static createContainer() {
    this.destroyExisting()
    this.container = document.createElement('div')
    document.body.appendChild(this.container)
    return this.container
  }

  private static destroyExisting() {
    if (this.instance) {
      this.instance.unmount()
      this.instance = null
    }
    if (this.container) {
      this.container.remove()
      this.container = null
    }
  }

  static show(message: string, type: 'info' | 'warning' | 'error' = 'info') {
    const container = this.createContainer()
    
    const app = createApp({
      render() {
        return h(MessageBoxComponent, {
          show: true,
          message,
          type,
          onClose: () => {
            MessageBoxService.destroyExisting()
          }
        })
      }
    })
    
    this.instance = app
    app.mount(container)
  }


  static async confirm(options: {
    title: string
    message: string
    confirmText?: string
    cancelText?: string
    type?: 'info' | 'warning' | 'error'
    extraButton?: { text: string, action: () => void }
    showRemarks?: boolean
    requireRemarks?: boolean
    showAttachments?: boolean
    requireAttachments?: boolean
    showInput?: boolean
    requireInput?: boolean
    inputCaption?: string
    inputType?: 'text' | 'number'
    path?: string
  }): Promise<any> {
    const container = this.createContainer()

    return new Promise((resolve) => {
      const app = createApp({
        render() {
          return h(MessageBoxComponent, {
            show: true,
            title: options.title,
            message: options.message,
            type: options.type || 'warning',
            showCancel: true,
            confirmText: options.confirmText || 'Confirm',
            cancelText: options.cancelText || 'Cancel',
            showAttachment: options.showAttachments || options.requireAttachments || false,
            showRemarks: options.showRemarks || options.requireRemarks || false,
            requireAttachment: options.requireAttachments || false,
            requireRemarks: options.requireRemarks || false,
            requireInput: options.requireInput || false,
            showInput: options.showInput || false,
            inputCaption: options.inputCaption || '',
            inputType: options.inputType || 'text',
            path: options.path || '',
            extraButton: options.extraButton ? { text: options.extraButton.text, event: 'extraButtonClick' } : undefined, // Ensure extraButton is always defined
            onConfirm: (...args: unknown[]) => {
              const [remarks, attachment, input] = args as [string, string | null, string | null];
              MessageBoxService.destroyExisting()
              if (options.requireAttachments && !attachment) {
                resolve({ confirmed: false })
              } else if (options.requireRemarks && !remarks) {
                resolve({ confirmed: false })
              } else if (options.requireAttachments || options.requireRemarks || options.showAttachments || options.showRemarks || 
                options.showInput || options.requireInput
              ) {
                resolve({ confirmed: true, remarks, attachment: attachment, input: input })                
              } else {
                resolve(true)
              }
            },
            onCancel: () => {
              MessageBoxService.destroyExisting()
              resolve(false)
            },
            onExtraButtonClick: () => {
              if (options.extraButton?.action) {
                options.extraButton.action()
              }
              MessageBoxService.destroyExisting()
            }
          })
        }
      })
      
      this.instance = app
      app.mount(container)
    })
  }
}

export default MessageBoxService